import { BearerAuthenticator, IBearerTokenInfo } from '../httpClient'
import { getActiveTokens } from '../../auth/utils.ts'

export interface NextPeakOrganizationBearerAuthenticatorOpts {
	getNewTokens: () => Promise<void>
}

export class NextPeakBearerAuthenticator extends BearerAuthenticator {
	constructor(opts: NextPeakOrganizationBearerAuthenticatorOpts) {
		const tokenExpirationShortageSeconds = 60
		let isRefreshing = false

		const authCallback = async (): Promise<IBearerTokenInfo> => {
			const currentTokens = getActiveTokens()
			// If current tokens are valid, return them
			if (
				!!currentTokens.organizationTokenExpiresAt &&
				new Date(currentTokens.organizationTokenExpiresAt * 1000) > new Date(new Date().getTime() + tokenExpirationShortageSeconds * 1000)
			) {
				return {
					token: currentTokens.organizationToken!,
					expiresInSeconds: Math.floor((currentTokens.organizationTokenExpiresAt * 1000 - Date.now()) / 1000),
				}
			}

			if (
				!!currentTokens.accessTokenExpiresAt &&
				new Date(currentTokens.accessTokenExpiresAt * 1000) > new Date(new Date().getTime() + tokenExpirationShortageSeconds * 1000)
			) {
				// If access token is valid but organization token is not, we can still use the access token
				return {
					token: currentTokens.accessToken!,
					expiresInSeconds: Math.floor((currentTokens.accessTokenExpiresAt * 1000 - Date.now()) / 1000),
				}
			}

			try {
				if (isRefreshing) {
					throw new Error('Token refresh already in progress')
				}
				isRefreshing = true
				await opts.getNewTokens()

				// Verify tokens were actually updated
				const newTokens = getActiveTokens()
				if (
					!newTokens.organizationToken ||
					!newTokens.organizationTokenExpiresAt ||
					!newTokens.accessToken ||
					!newTokens.accessTokenExpiresAt
				) {
					throw new Error('Failed to obtain either access token or organization token during refresh')
				}

				if (newTokens.organizationToken) {
					return {
						token: newTokens.organizationToken,
						expiresInSeconds: Math.floor((newTokens.organizationTokenExpiresAt - Date.now()) / 1000),
					}
				}

				return {
					token: newTokens.accessToken,
					expiresInSeconds: Math.floor((newTokens.accessTokenExpiresAt - Date.now()) / 1000),
				}
			} finally {
				isRefreshing = false
			}
		}

		super(authCallback, { tokenExpirationShortageSeconds })
	}
}
